import React, { useState } from 'react';
import styled from 'styled-components';
import { CommonButton } from '../../../Styles/CommonStyles';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import nameStringFn from '../../../data/nameStringFn';
import { useUpdateComplaintsMutation } from '../../../features/complaints/complaintsApiSlice';

const FormContainer = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  margin-top: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  width: 100%;
`;

const FormTitle = styled.h3`
  margin-bottom: 20px;
  color: #1976d2;
  font-size: 1.25rem;
`;

const FormRow = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  align-items: flex-start;
`;

const FormField = styled.div`
  flex: 1;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const Button = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
`;

const SubmitButton = styled(Button)`
  background-color: #4caf50;
`;

const CancelButton = styled(Button)`
  background-color: #f44336;
`;

const SupervisorComplaintForm = ({ 
  row, 
  remark, 
  setRemark, 
  auth, 
  getSingleuser, 
  supportusers,
  storeoffices,
  handleBack, 
  availableActions 
}) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedSupportStaff, setSelectedSupportStaff] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [updateComplaint] = useUpdateComplaintsMutation();

  // Get list of available support staff for reassignment
  const getAvailableSupportStaff = () => {
    if (!supportusers) return [];
    
    return Object.values(supportusers).filter(user => {
      // Check if user has SupportStaff role
      if (!user.roles?.includes('SupportStaff')) return false;
      
      // Exclude currently assigned user
      if (user._id === row.curr_assigned_to) return false;
      
      // Check if user has the complaint category
      if (!user?.complaintCategories || !Array.isArray(user.complaintCategories)) return false;
      
      return user.complaintCategories.some(cat => 
        cat?.toLowerCase() === row.complaintCat?.toLowerCase()
      );
    });
  };

  const availableSupportStaff = getAvailableSupportStaff();

  const handleActionSubmit = async () => {
    if (!selectedAction) {
      alert('Please select an action');
      return;
    }

    if (!remark.trim()) {
      alert('Please enter a remark');
      return;
    }

    // Check if reassignment action requires support staff selection
    if (selectedAction === 'REASSI' && !selectedSupportStaff) {
      alert('Please select a support staff member for reassignment');
      return;
    }

    // Check if store offices data is available
    if (!storeoffices || Object.keys(storeoffices).length === 0) {
      alert('Error: Store office data not available. Please refresh the page or contact administrator.');
      return;
    }

    setLoading(true);
    
    try {
      const actionData = availableActions.find(action => action.code === selectedAction);
      const nextAssignee = getNextAssignee(actionData);

      // Validate that nextAssignee is not null for actions that require forwarding
      if (!nextAssignee) {
        setLoading(false);
        alert('Error: Unable to determine next assignee. Please contact administrator.');
        return;
      }

      if (actionData.code === 'RESOLVEFWDL1' && !nextAssignee.id) {
        setLoading(false);
        alert(`Error: No L1Support found for store "${row.store}". Please contact administrator to assign L1Support for this store.`);
        return;
      }

      const isOnBehalfAction = actionData.code.startsWith('OBO_');

      const reqDataEntry = {
        action: actionData.action,
        sent_to_tag: actionData.sent_to_tag,
        text: remark,
        sent_by: {
          id: auth.id,
          name: supportusers[auth.id]?.name || {},
          designation: 'Supervisor'
        },
        sent_to: nextAssignee,
        time_stamp: new Date().toISOString()
      };

      // Add on_behalf_of field when supervisor acts on behalf of support staff
      if (isOnBehalfAction) {
        reqDataEntry.on_behalf_of = {
          id: row.curr_assigned_to,
          name: supportusers[row.curr_assigned_to]?.name || {},
          designation: 'Support Staff'
        };
      }

      const updateData = {
        id: row._id,
        req_data_entry: reqDataEntry,
        curr_status: actionData.updatedStatus,
        curr_sent_by: auth.id,
        curr_sent_to: nextAssignee?.id || auth.id,
        curr_sent_to_string: nextAssignee ? `${nameStringFn(nextAssignee)}`.trim() : 'Support Staff',
        curr_sent_to_tag: actionData.sent_to_tag,
        opened: actionData.code === 'SUPCLOSE'
      };      

      if (actionData.code === 'SUPCLOSE') {
        updateData.closed_at = new Date().toISOString();
      }

      if (actionData.code === 'REASSI') {
        updateData.curr_assigned_to = nextAssignee?.id;
      }

      await updateComplaint(updateData).unwrap();
      alert('Action completed successfully');
      handleBack();
    } catch (error) {
      console.error('Error updating complaint:', error);
      alert('Failed to submit action. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getNextAssignee = (actionData) => {
    // Logic for determining next assignee based on action
    const supportStaff = supportusers[row?.curr_assigned_to];

    if (!storeoffices) {
      console.error("storeoffices is null or undefined!");
      return null;
    }
    
    const storeOfficesArray = Object.values(storeoffices);
    const storeOffice = storeOfficesArray.find(x => x.storeoffice === row.store);
    const l1SupportUserId = storeOffice?.roleUsers?.L1Support;
    const l1SupportUser = l1SupportUserId && getSingleuser ? getSingleuser[l1SupportUserId] : null;
    
    switch (actionData.code) {
      case 'SUPCLOSE': // Close Complaint - send notification to original user
        return row.req_data?.[0]?.sent_by || null;

      case 'RESOLVEFWDL1': { // Resolved: Forward to L1Support for Closure
        if (!l1SupportUser || !l1SupportUserId) {
          console.error(`L1Support not found for store: ${row.store}`);
          return null;
        }
        return {
          id: l1SupportUserId,
          name: l1SupportUser.name || {},
          designation: l1SupportUser?.designation?.[row.store] || 'L1Support'
        };
      }

      case 'REASSI': {
        const selectedStaff = supportusers[selectedSupportStaff];
        return selectedStaff ? {
          id: selectedStaff._id || selectedStaff.id,
          name: selectedStaff.name || {},
          designation: 'Support Staff'
        } : null;
      }
      
      case 'RBDCSS':
        return supportusers && row?.curr_assigned_to ? {
          id: row?.curr_assigned_to,
          name: supportusers[row?.curr_assigned_to]?.name || {},
          designation: 'Support Staff'
        } : null;

      case 'OBO_SRCLSD':
        return {
          id: auth.id,
          name: supportusers[auth.id]?.name || {},
          designation: 'Supervisor'
        };

      case 'OBO_POH':
        return supportStaff ? {
          id: supportStaff._id || supportStaff.id,
          name: supportStaff.name || {},
          designation: 'Support Staff'
        } : null;
      
      default:
        return null;
    }
  };

  const showSupportStaffSelection = selectedAction === 'REASSI';

  return (
    <FormContainer>
      <FormTitle>Supervisor Actions</FormTitle>
      
      <FormRow>
        <FormField>
          <FormControl fullWidth>
            <InputLabel>Select Action</InputLabel>
            <Select
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              label="Select Action"
            >
              {availableActions.map((action) => (
                <MenuItem key={action.code} value={action.code}>
                  {action.action}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </FormField>
      </FormRow>

      {showSupportStaffSelection && (
        <FormRow>
          <FormField>
            <FormControl fullWidth>
              <InputLabel>Select Support Staff</InputLabel>
              <Select
                value={selectedSupportStaff}
                onChange={(e) => setSelectedSupportStaff(e.target.value)}
                label="Select Support Staff"
              >
                {availableSupportStaff.map((staff) => (
                  <MenuItem key={staff._id} value={staff._id}>
                    {nameStringFn(staff)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>
        </FormRow>
      )}

      <FormRow>
        <FormField>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Remarks"
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            placeholder="Enter your remarks here..."
            variant="outlined"
          />
        </FormField>
      </FormRow>

      <ButtonsWrapper>
        <SubmitButton
          onClick={handleActionSubmit}
          disabled={loading || !selectedAction || !remark.trim() || (showSupportStaffSelection && !selectedSupportStaff)}
        >
          {loading ? 'Submitting...' : 'Submit Action'}
        </SubmitButton>
        <CancelButton onClick={handleBack}>
          Cancel
        </CancelButton>
      </ButtonsWrapper>
    </FormContainer>
  );
};

export default SupervisorComplaintForm; 